<template>
  <v-container class="ma-auto categories__container <xs:wd-relative <xs:wd-z-regionListContainer" fluid>
    <client-only>
      <div v-if="isMobile" v-intersect="{ handler: onIntersect, options: { threshold: 0 } }" class="target-box" />
    </client-only>
    <category-info-sheet-mobile
      v-if="isMobile"
      :region="region"
      :category="category"
      :subcategory-id="subcategoryId"
      :day-mode="category.day_mode"
      :has-geolocation="hasGeolocation"
      :filters="filterOptions"
      :search-key-word="searchKeyWord"
      :total-count="totalCount"
      :is-fixed-top="isFixedTop"
      :is-branch-card-mode="isBranchCardMode"
      :first-level-category-id="firstLevelCategoryId"
      @category-change="handleSubcategorySelected"
      @update-condition="onConditionUpdate"
    />
    <category-info-sheet
      v-else
      :region="region"
      :category="category"
      :subcategory-id="subcategoryId"
      :day-mode="category.day_mode"
      :has-geolocation="hasGeolocation"
      :filters="filterOptions"
      :search-key-word="searchKeyWord"
      :total-count="totalCount"
      :is-branch-card-mode="isBranchCardMode"
      :first-level-category-id="firstLevelCategoryId"
      @category-change="handleSubcategorySelected"
      @update-condition="onConditionUpdate"
    />

    <div class="categories__wrapper">
      <!-- 商品列表 -->
      <v-sheet v-if="isBranchCardMode ? branchesInfo.branches.length > 0 : categoryProductInfo.products.length > 0" class="categories-list" rounded>
        <div class="categories-list__wrapper">
          <v-row v-if="loadingList" class="product-list">
            <v-col v-for="number in 20" :key="number" class="product-list__col" cols="12" md="3" sm="6">
              <div class="product__box">
                <v-skeleton-loader class="mx-auto" max-width="300" type="image, list-item-three-line" />
              </div>
            </v-col>
          </v-row>

          <v-row v-else class="product-list">
            <template v-if="isBranchCardMode">
              <v-col v-for="branch in branchesInfo.branches" :key="branch.bid" class="product-list__col" cols="12" md="3" sm="6">
                <div class="product__box">
                  <BranchCard :branch="branch" :bring-query="false" />
                </div>
              </v-col>
            </template>

            <template v-else>
              <v-col v-for="product in categoryProductInfo.products" :key="product.product_id" class="product-list__col" cols="12" md="3" sm="6">
                <div class="product__box">
                  <product-card :product="product" :bring-query="false" />
                </div>
              </v-col>
            </template>
          </v-row>

          <loading v-if="loadingList" class="list-loading" :width="72" :height="72" color="orange" />
        </div>

        <pagination
          class="mb-9"
          :page="pagingState.page"
          :visible="8"
          :total-count="totalCount"
          :size="pagingState.size"
          @update="updatePagingPage"
        />
      </v-sheet>
      <div v-else class="categories-list--empty">
        <p class="empty-text">{{ $t('filter_empty_message') }}</p>
      </div>
    </div>

    <releated-blogs-swiper v-if="relatedBlogs.length > 0" class="blogs-swiper" :items="relatedBlogs" />

    <v-sheet class="serp wd-mt-6 wd-px-5 wd-py-4">
      <h2 class="wd-mb-5 text-h6">{{ $t('all_faq') }}</h2>
      <QAndA :faqs="categoryFaqs" />
    </v-sheet>

    <JsonLdFaq :faq-items="categoryFaqs" />

    <!-- Funki 入口浮動按鈕 -->
    <div :class="isCsBubbleShown ? 'wd-bottom-19' : 'wd-bottom-5'" class="wd-fixed wd-w-full wd-z-100 wd-pointer-events-none wd-left-0">
      <div class="wd-flex wd-justify-end wd-transition-all wd-px-3">
        <div class="wd-flex wd-flex-col wd-justify-end wd-gap-1">
          <div class="wd-relative">
            <FunkiEntryFloatingButton v-if="isShowFunkiEntry" class="wd-pointer-events-auto" />
          </div>
        </div>
      </div>
    </div>
  </v-container>
</template>

<script setup lang="ts">
import { useABTestingStore } from '@/store-pinia/abtesting'
import { useGlobalStore } from '@/store-pinia/global'

import utils from '@/utils'
import { getCategoryDefaultFiltersFromQuery, getCategorySelectedFilterTagMapFromQueryString } from '@/utils/filter'
import { usePageView } from '@/composition/usePageView'
import { provideGtmSource } from '@/composition/useGtmSource'
import { useBreakpoints } from '@/composition/useBreakpoints'
import notificationComposition from '@/composition/notification'
import { useRegionSearchNavigation } from '@/composables/useRegionSearchNavigation'
import { useCustomerService } from '@/composables/useCustomerService'
import { useFunkiStore } from '@/store-pinia/funki'
import { useRouteQueryFilterSync } from '@/composables/useRouteQueryFilterSync'

import { GtmEventMap, GtmSource } from '@/view-models/gtm'
import { CategoryOnboard, CategoryType } from '@/view-models/category'
import { FilterTypes, CategorySortKey } from '@/constants/filters'
import { ASYNC_DATA_KEYS, getAsyncDataKey } from '@/constants/async-data-keys'

import PromoCampaignModal from '@/components/dialog/PromoCampaignModal.vue'
import CategoryInfoSheet from '@/components/page/categories/category-info-sheet.vue'
import CategoryInfoSheetMobile from '@/components/page/categories/mobile/category-info-sheet--mobile.vue'

const ProductCard = defineAsyncComponent(() => import('@/components/card/product.vue'))
const BranchCard = defineAsyncComponent(() => import('@/components/card/BranchCard.vue'))
const Pagination = defineAsyncComponent(() => import('@/components/pagination/pagination.vue'))
const ReleatedBlogsSwiper = defineAsyncComponent(() => import('@/components/swiper/related-blogs-swiper.vue'))
const Loading = defineAsyncComponent(() => import('@/components/icons/loading.vue'))
const QAndA = defineAsyncComponent(() => import('@/components/pageCategory/QAndA.vue'))
const JsonLdFaq = defineAsyncComponent(() => import('@/components/jsonld/faq.vue'))
const FunkiEntryFloatingButton = defineAsyncComponent(() => import('@/components/funki/FunkiEntryFloatingButton.vue'))

const nuxtApp = useNuxtApp()
const { $store: store, $pinia } = nuxtApp
const config = useRuntimeConfig()

const { isMobile } = useBreakpoints()

const loadingList = ref(false)
const isFixedTop = ref(false)

// FIXME: Please define the type of data
const relatedBlogs = ref<Array<unknown>>([])
// FIXME: 待確認是否移除
const blogSwiperIndex = ref(null)

const globalStore = useGlobalStore()
const aBTestingStore = useABTestingStore($pinia)

const i18n = useI18n()
const route = useRoute()
const router = useRouter()

const selectedFilters = computed(() => store.getters['filter/filters'])
const sortType = computed(() => store.getters['filter/sortType'])
const category = computed(() => nuxtApp.$store.getters['category/category'])
const region = computed(() => nuxtApp.$store.getters['region/region'])
const categoryFaqs = computed(() => nuxtApp.$store.getters['category/categoryFaqs'])
const categoryProductInfo = computed(() => nuxtApp.$store.getters['product/categoryProductInfo'])
const branchesInfo = computed(() => nuxtApp.$store.getters['branch/branchesInfo'])
const searchKeyWord = computed(() => nuxtApp.$store.getters['search/searchKeyword'])

const { sendGTMViewItemList } = useGTMViewItemList()

const isUrlSubcategoryPattern = computed(() => !!route.query.source_category_id)
const regionID = computed(() => Number(route.params.rid || 0))
const firstLevelCategoryId = computed(() => (isUrlSubcategoryPattern.value ? Number(route.query.source_category_id) : Number(route.params.id)))
const subcategoryId = computed(() => (isUrlSubcategoryPattern.value ? Number(route.params.id) : 0))

const loggedInUser = computed(() => nuxtApp.$auth.user)
const isBranchCardMode = computed(() => aBTestingStore.cardMode === 'branch')
const isSearchKeywordNotEmpty = computed(() => Boolean(searchKeyWord.value))
const totalCount = computed(() => (isBranchCardMode.value ? branchesInfo.value.totalCount : categoryProductInfo.value.totalCount))

useHead(() => {
  const categoryTitle = category.value?.page_title ?? ''
  const titleSuffix = i18n.t('funnow_title')
  const pageTitle = categoryTitle ? `${categoryTitle} | ${titleSuffix}` : `${titleSuffix}`
  const ogImage = utils.ogImageUrl(category.value?.image)
  const canonicalUrl = `${config.public.BASE_URL}${route.path}`
  return {
    title: pageTitle,
    meta: [
      { hid: 'title', name: 'title', content: pageTitle },
      { hid: 'description', name: 'description', content: category.value?.meta_desc },
      { hid: 'keywords', name: 'keywords', content: category.value?.meta_keywords },
      { hid: 'og:title', property: 'og:title', content: pageTitle },
      { hid: 'og:description', property: 'og:description', content: category.value?.meta_desc },
      { hid: 'og:image', property: 'og:image', content: ogImage },
    ],
    link: [{ hid: 'i18n-can', rel: 'canonical', href: canonicalUrl }],
  }
})

const { pushGiftExpiringNotification } = notificationComposition()
const { pagingState, userPosition, hasGeolocation } = useRegionSearchNavigation({
  fetchList: getProductList,
})

// Funki 入口顯示邏輯
const { shouldShowFunkiEntry: isShowFunkiEntry } = useFunkiStore()

// 客服氣泡顯示邏輯
const { isCsBubbleShown } = useCustomerService()

onMounted(() => {
  const promoCampaignId = route.query.campaign_id ? Number(route.query.campaign_id) : 0
  if (promoCampaignId) {
    globalStore.setRootDynamicComponentOption({ promoCampaignId })
    globalStore.setRootDynamicComponent(PromoCampaignModal)
    globalStore.setShouldRootDynamicComponentShow(true)
  }

  setNotification()
  sendGTMViewItemList()
})

onUnmounted(() => {
  nuxtApp.$store.dispatch('notification/resetNotification')
})

usePageView({ event: isUrlSubcategoryPattern.value ? GtmEventMap.SUB_CATEGORY_VIEWED : GtmEventMap.CATEGORY_VIEWED })
provideGtmSource(GtmSource.CATEGORY_LIST)

const { data } = await useAsyncData(getAsyncDataKey(ASYNC_DATA_KEYS.CATEGORY_PAGE_DATA, [regionID.value, route.params.id as string]), async () => {
  try {
    const query = route.query
    const isDataFromSubcategory = isUrlSubcategoryPattern.value && !query.filter_keyword

    await Promise.all([
      store.dispatch('region/getRegion', regionID.value),
      store.dispatch('category/getCategory', { categoryID: firstLevelCategoryId.value }),
      store.dispatch('category/getCategoryFaqs', { regionID: regionID.value, categoryID: firstLevelCategoryId.value }),
    ])

    const categoryInfo = store.state.category.category || {}
    store.dispatch('category/setFirstLevelCategory', categoryInfo)

    if (categoryInfo.web_onboard === CategoryOnboard.Hidden) {
      return nuxtApp.runWithContext(() => navigateTo('/', { replace: true }))
    }

    if (isDataFromSubcategory) {
      await Promise.all([
        store.dispatch('category/getCategory', { categoryID: subcategoryId.value, isFromSubcategory: true }),
        store.dispatch('category/getCategoryFaqs', { regionID: regionID.value, categoryID: subcategoryId.value }),
      ])
    }

    // fetch filter options and set filter info from query string
    const filtersOptionsRes = await fetchFilterOptions({
      categoryID: firstLevelCategoryId.value,
      regionID: regionID.value,
      ...(isDataFromSubcategory && { sub_category_id: subcategoryId.value }),
    })
    const selectedFilterTagMap =
      typeof query.filter_tags === 'string' ? getCategorySelectedFilterTagMapFromQueryString(query.filter_tags, filtersOptionsRes.filter_tags) : {}

    if (query.filter_keyword) {
      store.dispatch('search/setSearchKeyword', query.filter_keyword)
    }

    const filtersFromRouterQuery = getCategoryDefaultFiltersFromQuery(
      query as Record<string, string | unknown[]>,
      filtersOptionsRes,
      hasGeolocation.value
    )
    store.dispatch('filter/resetAndSetFilters', filtersFromRouterQuery)
    store.dispatch('filter/setFilterTags', selectedFilterTagMap)

    // Drop any query params that are not valid for the current subcategory's filterOptions.
    // Use replace: true so this does NOT create an extra history entry — the subcategory
    // switch itself (handleSubcategorySelected) used router.push to create the entry, so we
    // only need to clean the current entry's query params.
    const validFilterKeys = new Set(Object.keys(filtersFromRouterQuery))
    const hasDroppedKeys = Object.keys(query).some(k => k !== 'page' && !validFilterKeys.has(k))
    if (hasDroppedKeys) {
      await nuxtApp.runWithContext(() => navigateTo({ query: filtersFromRouterQuery as any }, { replace: true }))
    }

    // source_category_id is used by query string, filter store, and filterOptions
    // but the arg of API is `sub_category_id`, and add it already, so delete `source_category_id`(from filtersFromRouterQuery)
    const { source_category_id, ...filterData } = {
      sortby: sortType.value,
      filter_regionid: regionID.value,
      category_id: firstLevelCategoryId.value,
      size: pagingState.size,
      start: (pagingState.page - 1) * pagingState.size,
      ...filtersFromRouterQuery,
      ...(isDataFromSubcategory && { sub_category_id: subcategoryId.value }),
    }
    // fetch category items and related blogs
    await Promise.all([fetchCategoryItemsByCardMode(filterData), fetchRelatedBlogs()])

    return {
      filterOptions: filtersOptionsRes,
    }
  } catch (err) {
    console.error('useAsyncData error', err)
    throw showError({ statusCode: 404, message: 'Post not found', fatal: true })
  }
})

// FIXME: 定義型別
const filterOptions = computed(() => (data.value ? (data.value as any).filterOptions : {}))

watch(loggedInUser, (newUser, oldUser) => {
  if (newUser && !oldUser) {
    pushGiftExpiringNotification(newUser)
  }
})

// Sync filter store and re-fetch when only the query string changes (e.g. browser back/forward)
useRouteQueryFilterSync({
  filterOptions,
  hasGeolocation,
  selectedFilters,
  pagingState,
  updateSelectedFilters,
  getProductList,
  onBeforeSync(newQuery) {
    const selectedFilterTagMap =
      typeof newQuery.filter_tags === 'string'
        ? getCategorySelectedFilterTagMapFromQueryString(newQuery.filter_tags as string, filterOptions.value?.filter_tags)
        : {}
    store.dispatch('filter/setFilterTags', selectedFilterTagMap)

    if (newQuery.filter_keyword) {
      store.dispatch('search/setSearchKeyword', newQuery.filter_keyword)
    }
  },
})

// FIXME: Please define the type of data
function updateSelectedFilters(newPartialFilters: unknown) {
  store.dispatch('filter/setFilters', newPartialFilters)
}

function getListPayloadWithoutFilterOptions() {
  const startIndex = (pagingState.page - 1) * pagingState.size
  // FIXME: Please define the type of data
  let data: Record<string, unknown> = {
    sortby: sortType.value,
    filter_regionid: regionID.value,
    category_id: firstLevelCategoryId.value,
    size: pagingState.size,
    start: startIndex,
  }

  if (route.query.source_category_id && !isSearchKeywordNotEmpty.value) {
    data = {
      ...data,
      sub_category_id: subcategoryId.value,
    }
  }
  if (hasGeolocation.value) {
    data.lng = userPosition.lng
    data.lat = userPosition.lat
  }

  if (searchKeyWord.value) {
    data.filter_keyword = searchKeyWord.value
  }
  return data
}

async function getProductList() {
  // FIXME: 這邊的 page 是從query string取得，但目前流程會在 usePagination route.query.page 設定後立即呼叫 getProductList，所以這邊的page會是undefined

  const payloadWithoutFilterOptions = getListPayloadWithoutFilterOptions()

  try {
    loadingList.value = true
    // "source_category_id" is used by query string, filter store, and selectedFilters
    // but the arg of API is `sub_category_id`, and add it already, so delete `source_category_id`(from selectedFilters)
    const { source_category_id, page, ...payload } = {
      ...payloadWithoutFilterOptions,
      ...selectedFilters.value,
    }
    await fetchCategoryItemsByCardMode(payload)
  } catch (error) {
    console.error(error)
  } finally {
    loadingList.value = false
  }
}

function setNotification() {
  if (loggedInUser.value) {
    pushGiftExpiringNotification(loggedInUser.value)
  }
}

function onIntersect(isIntersecting: boolean) {
  const isFixed = !isIntersecting
  isFixedTop.value = isFixed
}

// FIXME: Please define the type of data
function handleSubcategorySelected(selectedCategoryInfo: { id: number; type: CategoryType }) {
  const { page, source_category_id, filter_keyword, ...otherFilterQuery } = route.query
  const isSelectedCategoryTab = selectedCategoryInfo.type === CategoryType.Category
  const pathName = isSelectedCategoryTab ? 'regions-rid-categories-id' : 'regions-rid-themes-id'
  const isSelectedAllTab = selectedCategoryInfo.id === firstLevelCategoryId.value

  // reset keyword only, keep filters when switching subcategory
  nuxtApp.$store.dispatch('search/setSearchKeyword', '')

  loadingList.value = true
  const params = { rid: regionID.value, id: selectedCategoryInfo.id }
  router.push(
    nuxtApp.$localePath({
      name: pathName,
      params,
      query: { ...otherFilterQuery, ...(isSelectedAllTab ? {} : { source_category_id: firstLevelCategoryId.value }) },
    })
  )
}

// sort 目前在 component 內部呼叫 store 處理更新，所以這邊只需要 fetch data
function onConditionUpdate({ sort, filter }: { sort: CategorySortKey; filter: Record<FilterTypes, string | Array<string>> }) {
  const currentPage = pagingState.page
  const firstPage = 1
  // Preserve source_category_id in the query string when applying filters in subcategory mode.
  // filter may come from a partial source (e.g. search bar) that doesn't include source_category_id,
  // so we always forward it from the current route query to avoid dropping it.
  const sourceCategoryId = route.query.source_category_id
  const newQuery = {
    ...filter,
    page: firstPage,
    ...(sourceCategoryId ? { source_category_id: sourceCategoryId } : {}),
  }

  updateSelectedFilters(newQuery)
  updatePagingPage(firstPage)
  navigateTo({ query: newQuery })

  if (currentPage === firstPage) {
    getProductList()
  }
}

// FIXME: Please define the type of data
async function fetchCategoryItemsByCardMode(params: unknown) {
  const dispatchAction = isBranchCardMode.value ? 'branch/getBranchList' : 'product/getProductList'
  await nuxtApp.$store.dispatch(dispatchAction, params)
  sendGTMViewItemList()
}

// 相關文章區塊
// FIXME: Please define the type of data
function fetchRelatedBlogs() {
  const blogIDs = (category.value?.blog_articles?.split(',') || []).map((id: string) => id.trim())

  return blogIDs.filter(Boolean).map((blogID: string) =>
    nuxtApp.$store
      .dispatch('category/getBlogsArticle', { blogID: blogID })
      .then((res: any) => {
        if (res.code === 0) {
          relatedBlogs.value.push(res.data)
        }
      })
      .catch((error: Error) => {
        console.error('getBlogArticle:error', blogID, error)
      })
  )
}
// FIXME: Please define the type of data
async function fetchFilterOptions(params: unknown) {
  try {
    const filtersRes = await store.dispatch('filter/getFilters', params)

    return filtersRes.code === 0 ? filtersRes.data : {}
  } catch (error) {
    console.error('fetchFilterOptions: error', error)

    return {}
  }
}

function updatePagingPage(newPage: number) {
  pagingState.page = newPage
}
</script>
<style scoped lang="scss" src="@/assets/sass/pages/categories.scss"></style>
